import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { remove } from "lodash";

import { TDateISO } from "~/@types/TDateISO";
import {
	AddLineItemDataInput,
	CreateAddress,
	LineItemData,
	OrderContactDataInput,
	OrderCustomerDataInput,
	OrderStateData,
	OrderType
} from "~/gql/graphqlBase.generated";
import { DEFAULT_FULFILLMENT_SOURCE } from "~/helpers/fulfillmentSource";
import { toLocalDateTimeIso } from "~/helpers/timestampFormat";
import { type NewOrderAttempt } from "~fulfill/orders/helpers/newOrderAttempt";

export interface OrderInputs {
	carrier?: string;
	containerCode?: string;
	expectedDeliveryDate: TDateISO;
	finalCredit?: boolean;
	orderId: string;
	orderNumber: string;
	poNumber: string;
	produceAllocate?: boolean;
	route?: string;
	salesPersonEmail: string;
	/** Per-order override of the order-confirmation-email toggle. null/undefined = inherit from location/customer. */
	sendOrderEmails?: boolean | null;
	/** Per-order override of the invoice-email toggle. null/undefined = inherit from location/customer. */
	sendInvoiceEmails?: boolean | null;
	/** Structured ship-from site (WorkSuite-native SiteLocationId). Selected via SelectSite at order creation. */
	shipFromSiteLocationId?: string;
	/** Display name of the selected ship-from site — mirrored into the legacy name-only fromLocation field. */
	shipFromSiteName?: string;
	tenantId: string;
	termsId?: string;
	type: OrderType;
}

// wsapi accepts this reason on the add/update commands but LineItemData does not carry it back.
interface OrderMinimumOverride {
	minimumOverrideReason?: null | string;
}

export interface OrderManualPrice {
	isManualPrice?: boolean;
}

export type OrderFormLineItem = LineItemData & OrderManualPrice & OrderMinimumOverride;

export type StagedLineItem = AddLineItemDataInput &
	OrderManualPrice &
	OrderMinimumOverride &
	Partial<
		Pick<
			LineItemData,
			| "acknowledgedQuantity"
			| "acknowledgedZeroBillsNothing"
			| "orderedEaches"
			| "orderedQuantity"
			| "orderedUnits"
			| "shippedQuantity"
			| "shippedZeroBillsNothing"
			| "upgrades"
		>
	>;

interface OrderState {
	addLineItem: StagedLineItem;
	addLineItemOpen: boolean;
	addLineItemsTableOpen: boolean;
	currency: string;
	isFetching: boolean;
	lineItems?: OrderFormLineItem[];
	newOrderAttempt?: NewOrderAttempt;
	orderCustomer?: OrderCustomerDataInput;
	orderInputs: OrderInputs;
	orderStateData?: OrderStateData;
	pickedCustomerId?: string;
}

export const makeInitialOrderInputs = (): OrderInputs => ({
	expectedDeliveryDate: toLocalDateTimeIso(new Date()),
	orderId: "",
	orderNumber: "",
	poNumber: "",
	salesPersonEmail: "",
	tenantId: "",
	type: OrderType.Po
});

const initialState: OrderState = {
	addLineItem: {
		addedBy: "",
		cost: 0.0,
		currency: "",
		dateAdded: new Date().toISOString() as TDateISO,
		eachesQuantity: undefined,
		handlingUom: undefined,
		id: "",
		itemGroup: undefined,
		itemId: "",
		lineNumber: 0,
		name: "",
		overridePrice: undefined,
		price: 0.0,
		quantity: undefined,
		sku: "",
		upc: ""
	},
	addLineItemOpen: false,
	addLineItemsTableOpen: false,
	currency: "USD",
	isFetching: false,
	orderInputs: makeInitialOrderInputs()
};

export const orderSlice = createSlice({
	initialState,
	name: "order",
	reducers: {
		addLineItem: (state, action: PayloadAction<{ shouldKeepEntry?: boolean } | undefined>) => {
			const noteText = state.addLineItem.noteText?.trim();
			const lineItem = {
				...state.addLineItem,
				acknowledgedZeroBillsNothing: false,
				available: 0,
				availableByLocation: [],
				billableEaches: 0,
				creditableEaches: 0,
				creditedEaches: 0,
				eaches: 0,
				fulfillmentSource: state.addLineItem.fulfillmentSource ?? DEFAULT_FULFILLMENT_SOURCE,
				lineSubtotal: 0,
				note: noteText
					? {
							noteText,
							submitDate: new Date().toISOString() as TDateISO,
							submittedBy: state.addLineItem.addedBy || ""
						}
					: undefined,
				notes: [],
				quantityText: "",
				resolvedEachesPerUnit: 1,
				royaltyExempt: state.addLineItem.royaltyExempt ?? false,
				shippedZeroBillsNothing: false,
				sku: state.addLineItem.sku ?? "",
				stageBasisQuantity: 0,
				tag1Locked: false,
				tag2Locked: false,
				upc: state.addLineItem.upc ?? "",
				upgradeSubtotalAdjustment: 0,
				upgrades: []
			};
			const stagedLine = action.payload?.shouldKeepEntry
				? state.lineItems?.find((item) => item.id === state.addLineItem.id)
				: undefined;
			const numberedLines = [...(state.orderStateData?.lineItems ?? []), ...(state.lineItems ?? [])];
			const maxLineNumber = Math.max(0, ...numberedLines.map((item) => item.lineNumber || 0));
			state.lineItems = [
				...(state.lineItems ?? []).filter((item) => !stagedLine || item.id !== stagedLine.id),
				{
					...lineItem,
					lineNumber: stagedLine?.lineNumber ?? maxLineNumber + 1,
					sku: state.addLineItem.sku ?? "",
					upc: state.addLineItem.upc ?? ""
				}
			];
			if (!action.payload?.shouldKeepEntry) state.addLineItem = initialState.addLineItem;
		},
		clearOrderInfo: (state) => {
			state.addLineItem = initialState.addLineItem;
			state.addLineItemOpen = false;
			state.addLineItemsTableOpen = false;
			state.currency = initialState.currency;
			state.isFetching = initialState.isFetching;
			state.lineItems = undefined;
			state.newOrderAttempt = undefined;
			state.orderCustomer = undefined;
			state.orderInputs = makeInitialOrderInputs();
			state.orderStateData = undefined;
			state.pickedCustomerId = undefined;
		},
		closeAddLineItem: (state) => {
			state.addLineItemOpen = false;
		},
		closeAddLineItemsTable: (state) => {
			state.addLineItemsTableOpen = false;
		},
		openAddLineItem: (state) => {
			state.addLineItemOpen = true;
		},
		openAddLineItemsTable: (state) => {
			state.addLineItemsTableOpen = true;
		},
		removeLineItem: (state, action: PayloadAction<string>) => {
			if (!state.lineItems) return;
			remove(state.lineItems, (item) => item.id === action.payload);
			state.addLineItem = initialState.addLineItem;
			state.addLineItemOpen = false;
		},
		setAddLineItem: (state, action: PayloadAction<Partial<StagedLineItem> | null>) => {
			if (action.payload === null) {
				state.addLineItem = initialState.addLineItem;
				return;
			}
			state.addLineItem = { ...initialState.addLineItem, ...action.payload };
		},
		setLineItems: (state, action: PayloadAction<LineItemData[] | null>) => {
			if (action.payload === null) {
				state.lineItems = undefined;
				return;
			}
			state.lineItems = [...action.payload];
		},
		setNewOrderAttempt: (state, action: PayloadAction<NewOrderAttempt | null>) => {
			state.newOrderAttempt = action.payload ?? undefined;
		},
		setOrderCustomer: (state, action: PayloadAction<OrderCustomerDataInput | null>) => {
			if (action.payload === null) {
				state.orderCustomer = undefined;
				return;
			}
			state.orderCustomer = { ...action.payload };
		},
		setOrderFetching: (state, action: PayloadAction<boolean>) => {
			state.isFetching = action.payload;
		},
		setOrderInputs: (state, action: PayloadAction<Partial<OrderInputs> | null>) => {
			if (action.payload === null) {
				state.orderInputs = makeInitialOrderInputs();
				return;
			}
			state.orderInputs = { ...makeInitialOrderInputs(), ...action.payload };
		},
		setPickedCustomerId: (state, action: PayloadAction<null | string>) => {
			state.pickedCustomerId = action.payload ?? undefined;
		},
		setOrderStateData: (state, action: PayloadAction<OrderStateData | null>) => {
			if (action.payload === null) {
				state.orderStateData = undefined;
				return;
			}
			state.orderStateData = { ...action.payload };
		},
		updateAddLineItem: (state, action: PayloadAction<Partial<StagedLineItem>>) => {
			state.addLineItem = { ...state.addLineItem, ...action.payload };
		},
		updateLineItem: (state, action: PayloadAction<AddLineItemDataInput>) => {
			const id = action.payload.id;
			const lineItem = state.lineItems?.find((item) => item.id === id);

			if (lineItem) {
				Object.assign(lineItem, action.payload);
			}
			state.addLineItem = initialState.addLineItem;
			state.addLineItemOpen = false;
		},
		updateOrderBillingAddress: (state, action: PayloadAction<CreateAddress>) => {
			if (!state.orderCustomer) return;
			state.orderCustomer.billToAddress = { ...action.payload };
		},
		updateOrderContact: (state, action: PayloadAction<Partial<OrderContactDataInput>>) => {
			if (!state.orderCustomer) return;
			Object.assign(state.orderCustomer.contact, action.payload);
		},
		updateOrderCustomer: (state, action: PayloadAction<Partial<OrderCustomerDataInput>>) => {
			if (!state.orderCustomer) return;
			state.orderCustomer = { ...state.orderCustomer, ...action.payload };
		},
		updateOrderInputs: (state, action: PayloadAction<Partial<OrderInputs>>) => {
			Object.assign(state.orderInputs, action.payload);
		},
		updateOrderLocationAddress: (state, action: PayloadAction<CreateAddress>) => {
			if (!state.orderCustomer) return;
			state.orderCustomer.location.address = { ...action.payload };
		}
	}
});

export const {
	addLineItem,
	clearOrderInfo,
	closeAddLineItem,
	closeAddLineItemsTable,
	openAddLineItem,
	openAddLineItemsTable,
	removeLineItem,
	setAddLineItem,
	setLineItems,
	setNewOrderAttempt,
	setOrderCustomer,
	setOrderFetching,
	setOrderInputs,
	setOrderStateData,
	setPickedCustomerId,
	updateAddLineItem,
	updateLineItem,
	updateOrderBillingAddress,
	updateOrderContact,
	updateOrderCustomer,
	updateOrderInputs,
	updateOrderLocationAddress
} = orderSlice.actions;

export default orderSlice.reducer;
